import firebase, { firestore, storage, geofirestore, } from "firebaseHelpers";
import { config, } from 'config/firebaseConfig'
import Moment from 'moment'
import { ASKED_VERIFICATION, VERIFIED, REJECTED, IN_PROCESS, } from 'constants/userValidationStatus'
import { NOT_PAID, } from 'constants/withdrawalStatus'
import { UTC_OFFSET, } from 'constants/constants'
import { BOOKED, HAS_CHECKOUT_NEEDS_RATING, HAS_CHECKOUT, } from 'constants/bookingStatus'

export const signInUser = async (email, password) => {
  return new Promise(async (resolve, reject) => {
    try {
      await firebase.auth().signInWithEmailAndPassword(email, password)

      resolve()
    } catch (error) {
      reject(error.message)
    }
  })
}

export const getUserInfos = async (userId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const userInfos = await firestore.collection('users').doc(userId).get()
      const userData = userInfos.data()

      resolve({
        id: userId, 
        ...userData,
        lastWithdrawal: userData.lastWithdrawal ? userData.lastWithdrawal.toDate() : null,
      })
    } catch (error) {
      reject(error.message)
    }
  })
}

export const getUserInfosFromEmail = async (userEmail) => {
  return new Promise(async (resolve, reject) => {
    try {
      const userInfos = await firestore.collection('users').where('email', '==', userEmail).get()

      if (userInfos.docs.length === 0) {
        resolve({ id: 'null' })
      } else {
        resolve({ id: userInfos.docs[0].id, ...userInfos.docs[0].data() })
      }
    } catch (error) {
      reject(error.message)
    }
  })
}

const fetchUsersFilter = (filters) => {
  let usersQuery = firestore.collection('users')

  if (typeof filters.lastName !== 'undefined' && filters.lastName !== '') {
    usersQuery = usersQuery.where('lastNameInsensitive', '==', filters.lastName.toLowerCase())
  }
  if (typeof filters.firstName !== 'undefined' && filters.firstName !== '') {
    usersQuery = usersQuery.where('firstNameInsensitive', '==', filters.firstName.toLowerCase())
  }
  if (typeof filters.email !== 'undefined' && filters.email !== '') {
    usersQuery = usersQuery.where('email', '==', filters.email)
  }
  if (typeof filters.roles !== 'undefined' && filters.roles !== '') {
    usersQuery = usersQuery.where('roles', '==', filters.roles)
  }
  if (typeof filters.verificationStatus !== 'undefined' && filters.verificationStatus !== '') {
    usersQuery = usersQuery.where('verificationStatus', '==', Number(filters.verificationStatus))
  }
  if (typeof filters.managerFor !== 'undefined') {
    usersQuery = usersQuery.where('manager', 'array-contains', filters.managerFor)
  }
  if (typeof filters.gateManagerFor !== 'undefined') {
    usersQuery = usersQuery.where('gateManager', 'array-contains', filters.gateManagerFor)
  }
  return usersQuery
}

export const fetchUsers = async (filters) => {
  return new Promise(async (resolve, reject) => {
    try {
      const usersList = filters ? await fetchUsersFilter(filters).get() : await firestore.collection('users').get()

      resolve(usersList.docs.map((user) => {
        const userData = user.data()

        return {
          id: user.id,
          ...userData,
          created: userData.created ? userData.created.toDate() : null,
        }
      }).sort((a, b) => {
        if (a.created && !b.created) {
          return -1
        } else if (!a.created && b.created) {
          return 1
        }
        const momentA = Moment(a.created)
        const momentB = Moment(b.created)

        if (momentA.isAfter(momentB)) {
          return -1
        }
        if (momentA.isBefore(momentB)) {
          return 1
        }
        return 0
      }))
    } catch (error) {
      reject(error.message)
    }
  })
}

export const fetchReferredUsers = async (agenceId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const usersList = await firestore.collection('users').where('agenceReferral', '==', agenceId).get()

      resolve(usersList.docs.map((user) => {
        const userData = user.data()

        return {
          id: user.id,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          created: userData.created ? userData.created.toDate() : null,
        }
      }).sort((a, b) => {
        if (a.created && !b.created) {
          return -1
        } else if (!a.created && b.created) {
          return 1
        }
        const momentA = Moment(a.created)
        const momentB = Moment(b.created)

        if (momentA.isAfter(momentB)) {
          return -1
        }
        if (momentA.isBefore(momentB)) {
          return 1
        }
        return 0
      }))
    } catch (error) {
      reject(error.message)
    }
  })
}

export const createAccount = async (newUser) => {
  return new Promise(async (resolve, reject) => {
    try {
      const encodedToken = await firebase.auth().currentUser.getIdToken()
      fetch(`${config.httpURL}/createUserAccount`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${encodedToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newUser.email,
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          roles: newUser.roles,
        }),
      }).then(async (response) => {
        if (response.status !== 200) {
          throw response
        }
        const responseJson = await response.json()
        resolve(responseJson.id)
      }).catch(async (error) => {
        const errorJson = await error.json()
        reject(errorJson)
      })
    } catch (error) {
      reject(error)
    }
  })
}

export const deleteUser = async (userId) => {
  return new Promise(async (resolve, reject) => {
    try {
      await firestore.collection('users').doc(userId).delete()

      storage.ref().child(`usersID/${userId}.jpeg`).delete()
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchUserData = async (userId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const userInfos = await firestore.collection('users').doc(userId).get()
      const userData = userInfos.data()

      if (!userInfos.exists) {
        reject()
      }

      resolve({
        id: userId,
        ...userData,
        lastWithdrawal: userData.lastWithdrawal ? userData.lastWithdrawal.toDate() : null,
      })
    } catch (error) {
      reject(error)
    }
  })
}

export const editAccount = async (userId, editedUser) => {
  return new Promise(async (resolve, reject) => {
    try {
      const encodedToken = await firebase.auth().currentUser.getIdToken()
      fetch(`${config.httpURL}/editUserAccount`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${encodedToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: userId,
          email: editedUser.email,
          phoneNumber: editedUser.phoneNumber,
          firstName: editedUser.firstName,
          lastName: editedUser.lastName,
          roles: editedUser.roles,
          credit: editedUser.credit,
        }),
      }).then(async (response) => {
        if (response.status !== 200) {
          throw response
        }
        const responseJson = await response.json()
        resolve(responseJson.id)
      }).catch(async (error) => {
        const errorJson = await error.json()
        reject(errorJson)
      })
    } catch (error) {
      reject(error)
    }
  })
}

export const resetUserPassword = async (email) => {
  return new Promise(async (resolve, reject) => {
    try {
      await firebase.auth().sendPasswordResetEmail(email)

      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const askUserID = async (userId: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const user = firestore.collection('users').doc(userId)
      await user.set({ verificationStatus: ASKED_VERIFICATION, }, { merge: true })
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const validateUserID = async (userId: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const user = firestore.collection('users').doc(userId)
      await user.set({ verificationStatus: VERIFIED, }, { merge: true })
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const declineUserID = async (userId: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const user = firestore.collection('users').doc(userId)
      await user.set({ verificationStatus: REJECTED, }, { merge: true })
      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchManagers = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const managers = await firestore.collection('users').where('roles', '==', 'partner').get()

      resolve(managers.docs.map((user) => {
        return {
          id: user.id,
          email: user.data().email,
        }
      }))
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchGateManagers = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const gateManagers = await firestore.collection('users').where('roles', '==', 'gate_manager').get()

      resolve(gateManagers.docs.map((user) => {
        return {
          id: user.id,
          email: user.data().email,
        }
      }))
    } catch (error) {
      reject(error)
    }
  })
}

export const addManagerRoleToUsers = async (managers, parkingId) => {
  return Promise.all(managers.map((userId) => {
    return new Promise(async (resolve, reject) => {
      try {
        const user = firestore.collection('users').doc(userId)
        const userInfos = await user.get()

        if (!userInfos.exists) {
          resolve()
        }
        const userData = userInfos.data()

        if (!userData.manager || userData.manager.findIndex((parking) => (parking === parkingId)) === -1) {
          await user.set({
            manager: userData.manager ? [ ...userData.manager, parkingId ] : [parkingId],
          }, { merge: true })
          resolve()
        } else {
          resolve()
        }
      } catch (error) {
        console.log(error)
        resolve()
      }
    })
  }))
}

export const removeManagerRoleToUsers = async (managers, parkingId) => {
  return Promise.all(managers.map((userId) => {
    return new Promise(async (resolve, reject) => {
      try {
        const user = firestore.collection('users').doc(userId)
        const userInfos = await user.get()
        if (!userInfos.exists) {
          resolve()
        }
        const userData = userInfos.data()

        if (userData.manager && userData.manager.findIndex((parking) => (parking === parkingId)) !== -1) {
          await user.set({
            manager: userData.manager.filter((parking) => (parking !== parkingId))
          }, { merge: true })
          resolve()
        } else {
          resolve()
        }
      } catch (error) {
        resolve()
      }
    })
  }))
}

export const addGateManagerRoleToUsers = async (gateManagers, parkingId) => {
  return Promise.all(gateManagers.map((userId) => {
    return new Promise(async (resolve, reject) => {
      try {
        const user = firestore.collection('users').doc(userId)
        const userInfos = await user.get()

        if (!userInfos.exists) {
          resolve()
        }
        const userData = userInfos.data()

        if (!userData.gateManager || userData.gateManager.findIndex((parking) => (parking === parkingId)) === -1) {
          await user.set({
            gateManager: userData.gateManager ? [ ...userData.gateManager, parkingId ] : [parkingId],
          }, { merge: true })
          resolve()
        } else {
          resolve()
        }
      } catch (error) {
        console.log(error)
        resolve()
      }
    })
  }))
}

export const removeGateManagerRoleToUsers = async (gateManagers, parkingId) => {
  return Promise.all(gateManagers.map((userId) => {
    return new Promise(async (resolve, reject) => {
      try {
        const user = firestore.collection('users').doc(userId)
        const userInfos = await user.get()
        if (!userInfos.exists) {
          resolve()
        }
        const userData = userInfos.data()

        if (userData.gateManager && userData.gateManager.findIndex((parking) => (parking === parkingId)) !== -1) {
          await user.set({
            gateManager: userData.gateManager.filter((parking) => (parking !== parkingId))
          }, { merge: true })
          resolve()
        } else {
          resolve()
        }
      } catch (error) {
        resolve()
      }
    })
  }))
}

export const fetchWithdrawalsFilter = (filters, userId, parkingId, agenceId) => {
  let withdrawalsQuery = parkingId ? firestore.collection('parkings').doc(parkingId).collection('withdrawals') : firestore.collection('withdrawals')

  if (filters) {
    if (typeof filters.status !== 'undefined' && filters.status !== '') {
      withdrawalsQuery = withdrawalsQuery.where('status', '==', Number(filters.status))
    }
    if (typeof filters.fromTo.startDate !== 'undefined' && filters.fromTo.startDate !== '') {
      withdrawalsQuery = withdrawalsQuery.where('date', '>=', filters.fromTo.startDate.toDate())
    }
    if (typeof filters.fromTo.endDate !== 'undefined' && filters.fromTo.endDate !== '') {
      withdrawalsQuery = withdrawalsQuery.where('date', '<', filters.fromTo.endDate.toDate())
    }
  }
  if (userId) {
    withdrawalsQuery = withdrawalsQuery.where('customerId', '==', userId)
  }
  if (agenceId) {
    withdrawalsQuery = withdrawalsQuery.where('agenceId', '==', agenceId)
  }
  return withdrawalsQuery
}

export const fetchWithdrawals = async (filters, userId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const withdrawals = await fetchWithdrawalsFilter(filters, userId).get()

      resolve(withdrawals.docs.map((withdrawal) => {
        const withdrawalData = withdrawal.data()
        return {
          ...withdrawalData,
          id: withdrawal.id,
          date: withdrawalData.date.toDate(),
        }
      }).sort((a, b) => {
        const momentA = Moment(a.date)
        const momentB = Moment(b.date)

        if (momentA.isAfter(momentB)) {
          return -1
        }
        if (momentA.isBefore(momentB)) {
          return 1
        }
        return 0
      }))
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchAgenceWithdrawals = (filters, agenceId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const withdrawals = await fetchWithdrawalsFilter(filters, null, null, agenceId).get()

      resolve(withdrawals.docs.map((withdrawalSnap) => {
        const data = withdrawalSnap.data()

        return {
          ...data,
          id: withdrawalSnap.id,
          date: data.date.toDate(),          
        }
      }).sort((a, b) => {
        const momentA = Moment(a.date)
        const momentB = Moment(b.date)

        if (momentA.isAfter(momentB)) {
          return -1
        }
        if (momentA.isBefore(momentB)) {
          return 1
        }
        return 0
      }))
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchPartnerWithdrawals = (filters, parkingsId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const totalWithdrawals = await Promise.all(parkingsId.map((parkingId) => {
        return new Promise(async (resolve, reject) => {
          const parkingWithdrawals = await fetchWithdrawalsFilter(filters, null, parkingId).get()

          resolve(parkingWithdrawals.docs.map((parkingWithdrawal) => {
            const parkingWithdrawalData = parkingWithdrawal.data()
            return {
              ...parkingWithdrawalData,
              id: parkingWithdrawal.id,
              date: parkingWithdrawalData.date.toDate(),
            }
          }))
        })
      }))

      resolve(totalWithdrawals.reduce((acc, withdrawalData) => {
        return [ ...acc, ...withdrawalData ]
      }, []).sort((a, b) => {
        const momentA = Moment(a.date)
        const momentB = Moment(b.date)

        if (momentA.isAfter(momentB)) {
          return -1
        }
        if (momentA.isBefore(momentB)) {
          return 1
        }
        return 0
      }))
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchWithdrawalData = (withdrawalId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const withdrawal = await firestore.collection('withdrawals').doc(withdrawalId).get()

      resolve({
        ...withdrawal.data(),
        id: withdrawal.id,
        date: withdrawal.data().date.toDate(),
      })
    } catch (error) {
      reject(error)
    }
  })
}

export const setWithdrawStatus = (withdrawalId, newStatus) => {
  return new Promise(async (resolve, reject) => {
    try {
      await firestore.collection('withdrawals').doc(withdrawalId).set({
        status: newStatus,
      }, { merge: true })

      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const removeUserParking = (userId, parkingId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const user = await firestore.collection('users').doc(userId).get()
      const userData = user.data()

      await firestore.collection('users').doc(userId).set({
        parkings: userData.parkings.filter((parking) => {
          if (parking.id === parkingId) {
            return false
          }
          return true
        }),
      }, { merge: true })

      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const addUserParking = (userId, parkingId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const user = await firestore.collection('users').doc(userId).get()
      const userData = user.data()

      await firestore.collection('users').doc(userId).set({
        parkings: [ ...userData.parkings, firestore.collection('parkings').doc(parkingId), ]
      }, { merge: true })

      resolve()
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchAgenceTodaysStats = (userInfos) => {
  const startDay = Moment.utc().utcOffset(UTC_OFFSET).startOf('day').toDate()
  const endDay = Moment.utc().utcOffset(UTC_OFFSET).endOf('day').toDate()

  return new Promise(async (resolve, reject) => {
    try {
      const bookings = await firestore.collection('reservations').where('status', 'in', [BOOKED, HAS_CHECKOUT, HAS_CHECKOUT_NEEDS_RATING]).where('agenceReferral', '==', userInfos.id).where('reservationDate', '>=', startDay).where('reservationDate', '<=', endDay).get()
      const nbBookings = bookings.docs.length
      console.log(nbBookings)
      const bookingsFormated = bookings.docs.map((booking) => {
        const data = booking.data()
        return {
          ...data,
          id: booking.id,
          from: data.from.toDate(),
          to: data.to.toDate(),
          reservationDate: data.reservationDate.toDate(),
        }
      })
      const todaysIncome = bookingsFormated.reduce((acc, booking) => {
        return booking.agenceFee ? acc + booking.agenceFee : acc + 0
      }, 0)
      resolve({ todaysIncome, nbBookings })
    } catch (error) {
      reject(error)
    }
  })
}

export const createAgenceWithdrawal = (userInfos, withdrawMoney) => {
  return new Promise(async (resolve, reject) => {
    try {
      let query = firestore.collection('withdrawals')
      const withdrawalDate = new Date()

      await query.add({
        amount: withdrawMoney,
        currency: 'AED',
        customerId: userInfos.id,
        agenceId: userInfos.id,
        customerRef: firestore.collection('users').doc(userInfos.id),
        date: withdrawalDate,
        firstName: userInfos.firstName,
        lastName: userInfos.lastName,
        status: 0,
      })

      await firestore.collection('users').doc(userInfos.id).set({
        lastWithdrawal: withdrawalDate,
      }, { merge: true })

      resolve()
    } catch (error) {
      console.log(error)
      reject(error)
    }
  })
}

export const fetchAgenceRevenue = (userInfos) => {
  return new Promise(async (resolve, reject) => {
    try {
      let query = firestore.collection('reservations').where('agenceReferral', '==', userInfos.id)

      if (userInfos.lastWithdrawal) {
        query = query.where('reservationDate', '>', userInfos.lastWithdrawal)
      }
      const bookings = await query.get()
      const bookingsFormated = bookings.docs.map((booking) => {
        const data = booking.data()
        console.log(data)
        return {
          ...data,
          id: booking.id,
          from: data.from.toDate(),
          to: data.to.toDate(),
          reservationDate: data.reservationDate.toDate(),
        }
      })

      resolve(bookingsFormated.reduce((acc, booking) => {
        return booking.agenceFee ? acc + booking.agenceFee : acc + 0
      }, 0))
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchPartnerRevenue = (userInfos) => {
  return new Promise(async (resolve, reject) => {
    try {
      const parkingsInfos = await Promise.all(userInfos.manager.map((parkingId) => {
        return new Promise(async (resolve, reject) => {
          const parkingData = await geofirestore.collection('parkings').doc(parkingId).get()

          resolve({
            id: parkingId,
            lastWithdrawal: parkingData.data().lastWithdrawal ? parkingData.data().lastWithdrawal.toDate() : null,
          })
        })
      }))

      const bookingsData = await Promise.all(parkingsInfos.map((parkingData) => {
        return new Promise(async (resolve, reject) => {
          let query = firestore.collection('parkings').doc(parkingData.id).collection('reservations')

          if (parkingData.lastWithdrawal) {
            query = query.where('date', '>', parkingData.lastWithdrawal)
          }
          const bookings = await query.get()
          const bookingsFormated = bookings.docs.map((booking) => {
            const data = booking.data()
            return {
              ...data,
              id: booking.id,
              from: data.from.toDate(),
              to: data.to.toDate(),
              reservationDate: data.reservationDate.toDate(),
            }
          })
          resolve(bookingsFormated)
        })
      }))
      resolve(bookingsData.reduce((acc, bookings) => {
        const totalDue = bookings.reduce((acc2, booking) => {
          return acc2 + booking.refundablePartner
        }, 0)
        return acc + totalDue
      }, 0))
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchMonthRevenue = (userInfos) => {
  return new Promise(async (resolve, reject) => {
    try {
      const startMonth = Moment.utc().utcOffset(UTC_OFFSET).startOf('month').toDate()
      const endMonth = Moment.utc().utcOffset(UTC_OFFSET).endOf('month').toDate()
      const parkingData = await geofirestore.collection('parkings').get()

      const bookingsData = await Promise.all(parkingData.docs.map((parking) => {
        return new Promise(async (resolve, reject) => {
          const bookings = await firestore.collection('parkings').doc(parking.id).collection('reservations').where('reservationDate', '>=', startMonth).where('reservationDate', '<=', endMonth).get()
          const bookingsFormated = bookings.docs.map((booking) => {
            const data = booking.data()
            return {
              ...data,
              id: booking.id,
              from: data.from.toDate(),
              to: data.to.toDate(),
              reservationDate: data.reservationDate.toDate(),
            }
          })
          resolve(bookingsFormated)
        })
      }))
      resolve(bookingsData.reduce((acc, bookings) => {
        const totalDue = bookings.reduce((acc2, booking) => {
          return { totalPrice: acc2.totalPrice + booking.totalPrice, totalProfit: acc2.totalProfit + (booking.totalPrice - booking.refundablePartner), }
        }, { totalPrice: 0, totalProfit: 0, })
        return { totalPrice: acc.totalPrice + totalDue.totalPrice, totalProfit: acc.totalProfit + totalDue.totalProfit, }
      }, { totalPrice: 0, totalProfit: 0, }))
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchUserTodaysStats = (userInfos) => {
  const startDay = Moment.utc().utcOffset(UTC_OFFSET).startOf('day').toDate()
  const endDay = Moment.utc().utcOffset(UTC_OFFSET).endOf('day').toDate()
  let nbBookings = 0

  return new Promise(async (resolve, reject) => {
    try {
      const bookingsData = await Promise.all(userInfos.parkings.map((parkingRef) => {
        return new Promise(async (resolve, reject) => {
          const bookings = await firestore.collection('parkings').doc(parkingRef.id).collection('reservations').where('reservationDate', '>=', startDay).where('reservationDate', '<=', endDay).get()

          nbBookings += bookings.docs.length
          const bookingsFormated = bookings.docs.map((booking) => {
            const data = booking.data()
            return {
              ...data,
              id: booking.id,
              from: data.from.toDate(),
              to: data.to.toDate(),
              reservationDate: data.reservationDate.toDate(),
            }
          })
          resolve(bookingsFormated)
        })
      }))
      const todaysIncome = bookingsData.reduce((acc, bookings) => {
        const totalDue = bookings.reduce((acc2, booking) => {
          return acc2 + booking.refundablePartner
        }, 0)
        return acc + totalDue
      }, 0)
      resolve({ todaysIncome, nbBookings })
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchPartnerTodaysStats = (userInfos) => {
  const startDay = Moment.utc().utcOffset(UTC_OFFSET).startOf('day').toDate()
  const endDay = Moment.utc().utcOffset(UTC_OFFSET).endOf('day').toDate()
  let nbBookings = 0

  return new Promise(async (resolve, reject) => {
    try {
      const bookingsData = await Promise.all(userInfos.manager.map((parkingId) => {
        return new Promise(async (resolve, reject) => {
          const bookings = await firestore.collection('parkings').doc(parkingId).collection('reservations').where('reservationDate', '>=', startDay).where('reservationDate', '<=', endDay).get()

          nbBookings += bookings.docs.length
          const bookingsFormated = bookings.docs.map((booking) => {
            const data = booking.data()
            return {
              ...data,
              id: booking.id,
              from: data.from.toDate(),
              to: data.to.toDate(),
              reservationDate: data.reservationDate.toDate(),
            }
          })
          resolve(bookingsFormated)
        })
      }))
      const todaysIncome = bookingsData.reduce((acc, bookings) => {
        const totalDue = bookings.reduce((acc2, booking) => {
          return acc2 + booking.refundablePartner
        }, 0)
        return acc + totalDue
      }, 0)
      resolve({ todaysIncome, nbBookings })
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchAdminTodaysStats = (userInfos) => {
  const startDay = Moment.utc().utcOffset(UTC_OFFSET).startOf('day').toDate()
  const endDay = Moment.utc().utcOffset(UTC_OFFSET).endOf('day').toDate()
  let nbBookings = 0

  return new Promise(async (resolve, reject) => {
    try {
      const parkingData = await geofirestore.collection('parkings').get()

      const bookingsData = await Promise.all(parkingData.docs.map((parking) => {
        return new Promise(async (resolve, reject) => {
          const bookings = await firestore.collection('parkings').doc(parking.id).collection('reservations').where('reservationDate', '>=', startDay).where('reservationDate', '<=', endDay).get()

          nbBookings += bookings.docs.length
          const bookingsFormated = bookings.docs.map((booking) => {
            const data = booking.data()
            return {
              ...data,
              id: booking.id,
              from: data.from.toDate(),
              to: data.to.toDate(),
              reservationDate: data.reservationDate.toDate(),
            }
          })
          resolve(bookingsFormated)
        })
      }))
      const todaysIncome = bookingsData.reduce((acc, bookings) => {
        const totalDue = bookings.reduce((acc2, booking) => {
          return acc2 + booking.refundablePartner
        }, 0)
        return acc + totalDue
      }, 0)
      resolve({ todaysIncome, nbBookings })
    } catch (error) {
      reject(error)
    }
  })
}

export const fetchUserNeedValidation = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const users = await firestore.collection('users').where('verificationStatus', '==', IN_PROCESS).get()

      resolve(users.docs.length)
    } catch (error) {
      reject(error.message)
    }
  })
}

export const fetchWithdrawalsPending = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const withdrawals = await firestore.collection('withdrawals').where('status', '==', NOT_PAID).get()

      resolve(withdrawals.docs.length)
    } catch (error) {
      reject(error.message)
    }
  })
}

export const generateAgenceCode = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      // const withdrawals = await firestore.collection('withdrawals').where('status', '==', NOT_PAID).get()

      // resolve(withdrawals.docs.length)
    } catch (error) {
      // reject(error.message)
    }
  })
}